import React, { useCallback, useState, useRef, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import styled from "@emotion/styled";

import { COLORS, COLORS_BASE } from "../../utils/consts";

import { searchParamsObj } from "../../utils/dataHandler";
import { TouchEventHandler } from "react";
import TooltipWrapper from "./TooltipWrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type SliderProps = {
  min: number;
  max: number;
  diff?: number;
  minValInit?: number;
  maxValInit?: number;
  units: string;
  name: string;
  ariaLabel: string;
  urlParam: string;
  includeGreaterThanMax?: boolean, 
  optionalSetState?: React.Dispatch<React.SetStateAction<number>>;
  toolTipText?: string;
};

function Slider({
  min,
  max,
  units,
  diff = 1,
  minValInit = 0,
  maxValInit = 10,
  name,
  ariaLabel,
  urlParam,
  includeGreaterThanMax = true,
  optionalSetState,
  toolTipText,
}: SliderProps) {
  const minValRef = useRef<HTMLInputElement | null>(null);
  const maxValRef = useRef<HTMLInputElement | null >(null);
  const range = useRef<HTMLDivElement | null>(null);
  const [minVal, setMinVal] = useState<number>(minValInit);
  const [maxVal, setMaxVal] = useState<number>(maxValInit);

  const [searchParams, setSearchParams] = useSearchParams();

  const handleSliderMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMinVal(Number(e.target.value));
      if (optionalSetState) {
        optionalSetState(Number(e.target.value));
      }
    },
    [optionalSetState]
  );
  
  const handleSliderMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMaxVal(Number(e.target.value));
      if (optionalSetState) {
        optionalSetState(Number(e.target.value));
      }
    },
    [optionalSetState]
  );

  const changeSearchParamsMin = useCallback((e: any) => {
    const value: number = Math.min(+e.target.value, Number((maxVal - diff).toFixed(1)));
    searchParams.set(`${urlParam}_min`, value.toString());
    setSearchParams(searchParams)
  }, [diff, maxVal, searchParams, setSearchParams, urlParam])

  const changeSearchParamsMax = useCallback((e: any) => {
    const value = Math.max(+e.target.value, Number((minVal + diff).toFixed(1)));
    searchParams.set(`${urlParam}_max`, value.toString());
    setSearchParams(searchParams)
  }, [diff, minVal, searchParams, setSearchParams, urlParam])


    // Set width of the range to decrease from the left side
    useEffect(() => {
        // if(inactive){return}
        if (maxValRef.current) {
            const minPercent = Math.round(((minVal - min) / (max - min)) * 100);
            const maxPercent = Math.round(((+maxValRef.current.value - min) / (max - min)) * 100);
            // const minPercent = getPercent(minVal);
            // const maxPercent = getPercent(+maxValRef.current.value); 
            if (range.current) {
                range.current.style.left = `${minPercent}%`;
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
        }, [minVal, max, min]);
  
      // Set width of the range to decrease from the right side
      useEffect(() => {
        // if(inactive){return}
        if (minValRef.current) {
          const minPercent = Math.round(((+minValRef.current.value - min) / (max - min)) * 100);
          const maxPercent = Math.round(((maxVal - min) / (max - min)) * 100);
          // const minPercent = getPercent(+minValRef.current.value);
          // const maxPercent = getPercent(maxVal);
          if (range.current) {
            range.current.style.width = `${maxPercent - minPercent}%`;
          }
        }
        }, [maxVal, max, min]);

  return (
    <SliderContainerStyle>
    <SliderNameStyle>{name}</SliderNameStyle>
    {toolTipText && 
    <TooltipWrapper content={toolTipText} positionFromDivBottom="35px" margin="0 0 0 5px">
      <FontAwesomeIcon icon="info-circle" /> 
    </TooltipWrapper>}
    <SliderContentStyle>
        <SliderThumbWrapperStyle>
            <SliderThumbStyle
                type="range"
                name={name}
                id={name}
                aria-label={ariaLabel}
                min={min}
                max={max}
                step={diff}
                value={minVal}
                ref={minValRef}
                onChange={handleSliderMinChange}
                onTouchEnd={changeSearchParamsMin}
                onMouseUp={changeSearchParamsMin}
            />
            <SliderThumbStyle
                thumbZIndex={4}
                type="range"
                name={name}
                id={name}
                aria-label={ariaLabel}
                min={min}
                max={max}
                step={diff}
                value={maxVal}
                ref={maxValRef}
                onChange={handleSliderMaxChange}
                onTouchEnd={changeSearchParamsMax}
                onMouseUp={changeSearchParamsMax}
            />
        </SliderThumbWrapperStyle>
        <SliderBarWrapperStyle>
            <SliderTrackStyle/> 
            <SliderRangeStyle ref={range}/> 
            <SliderLeftValueStyle>
                {minVal}
                {units && units}
            </SliderLeftValueStyle>
            <SliderRightValueStyle>
                {maxVal}
                {units && units}{((Number(maxVal)===max)&&includeGreaterThanMax)&&"+"}
            </SliderRightValueStyle>
        </SliderBarWrapperStyle>
      </SliderContentStyle>
    </SliderContainerStyle>
  );
}

export default Slider;

const SliderContainerStyle = styled.div<{}>(() => `
    display: flex;
    justify-content: space-between;
    padding: 15px 0;
    position: relative;
`);

const SliderNameStyle = styled.div<{}>(() => `
    
`);

const SliderContentStyle = styled.div<{}>(() => `
    padding: 0 5px;`);

const SliderThumbWrapperStyle = styled.div<{}>(() => `
    position: relative`);

const SliderBarWrapperStyle = styled.div<{}>(() => `
    position: relative;
    width: 205px; 
    height: 30px;`);

const SliderLeftValueStyle = styled.div<{}>(() => `
    color: ${COLORS_BASE.BRAND.DARKER};
    margin-top: 15px;
    position: absolute;
    left: 0px;
`);

const SliderRightValueStyle = styled.div<{}>(() => `
    color: ${COLORS_BASE.BRAND.DARKER};
    margin-top: 15px;
    position: absolute;
    right: 0px;
`);

const SliderThumbStyle = styled.input<{thumbZIndex?: number}>(({thumbZIndex=3}) => `
    z-index: ${thumbZIndex};
    pointer-events: none;
    position: absolute;
    height: 0;
    width: 210px; 
    outline: none;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
    ::-webkit-slider-thumb {
        -webkit-appearance: none;
        -webkit-tap-highlight-color: transparent;
        background-color: ${COLORS_BASE.GREYS.MAIN};
        border: none;
        border-radius: 50%;
        box-shadow: 0 0 1px 1px ${COLORS_BASE.GREYS.DARKER};
        cursor: pointer;
        height: 15px;
        width: 15px;
        margin-top: 5px;
        pointer-events: all;
        position: relative;
    }
    ::-moz-range-thumb {
        background-color: ${COLORS_BASE.GREYS.MAIN};
        border: none;
        border-radius: 50%;
        box-shadow: 0 0 1px 1px ${COLORS_BASE.GREYS.DARKER};
        cursor: pointer;
        height: 15px;
        width: 15px;
        pointer-events: all;
        /* position: relative; */
    }
    @-moz-document url-prefix() {
        top: 2px;
    }
`);

const SliderTrackStyle = styled.div<{}>(() => `
    border-radius: 5px;
    height: 5px;
    position: absolute;
    background-color: ${COLORS_BASE.GREYS.MAIN};
    width: 100%;
    z-index: 1;
    border: 1px solid ${COLORS_BASE.GREYS.DARKER.replace(`"`, ``)};
`);

const SliderRangeStyle = styled.div<{}>(() => `
    border-radius: 5px;
    height: 5px;
    position: absolute;
    z-index: 2;
    background: ${COLORS.SIDEBAR.HIGHLIGHT};
    border: 1px solid ${COLORS.SIDEBAR.HIGHLIGHT.replace(`"`, ``)};
`);